import { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency, formatQty } from "../lib/api";
import { Plus, Users, Truck, X, Mail, Phone, Crown, TrendingUp, Sparkles, Coffee, UserCog, Trash2, FileText, MessageCircle, Edit } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";

const TAG_META = {
  "VIP Collector": { color: "#D4AF37", bg: "rgba(212,175,55,0.12)", icon: Crown, blurb: "≥3 purchases · ≥Rp 100k LTV" },
  "Investor Buyer": { color: "#7BA585", bg: "rgba(123,165,133,0.12)", icon: TrendingUp, blurb: "≥Rp 50k avg ticket" },
  "High Potential Client": { color: "#C99356", bg: "rgba(201,147,86,0.12)", icon: Sparkles, blurb: "Rp 25k+ spent or large first buy" },
  "Casual Buyer": { color: "#8A8A8A", bg: "rgba(255,255,255,0.04)", icon: Coffee, blurb: "Occasional purchase" },
  "New": { color: "#666", bg: "rgba(255,255,255,0.03)", icon: Users, blurb: "No purchases yet" },
};

export default function CRM() {
  const { user } = useAuth();
  const canEdit = user?.role !== "accountant";
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [segmentSummary, setSegmentSummary] = useState({});
  const [tagFilter, setTagFilter] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [reassign, setReassign] = useState(null);

  const load = async () => {
    const tasks = [
      api.get("/crm/customers/segments"),
    ];
    // Suppliers and sales users only fetched for admin/accountant
    if (user?.role === "admin" || user?.role === "accountant") {
      tasks.push(api.get("/crm/suppliers"));
    }
    if (isAdmin) {
      tasks.push(api.get("/sales-users?status=Active"));
    }
    const results = await Promise.all(tasks);
    const seg = results[0];
    setCustomers(seg.data.customers);
    setSegmentSummary(seg.data.summary || {});
    let idx = 1;
    if (user?.role === "admin" || user?.role === "accountant") {
      setSuppliers(results[idx].data);
      idx += 1;
    }
    if (isAdmin) {
      setSalesUsers(results[idx].data);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sellerNameById = useMemo(() => {
    const m = {};
    salesUsers.forEach((s) => { m[s.id] = s.name; });
    return m;
  }, [salesUsers]);

  const openDetail = async (contact, kind) => {
    const ep = kind === "customer" ? `customers/${contact.id}/history` : `suppliers/${contact.id}/history`;
    const { data } = await api.get(`/crm/${ep}`);
    setDetail({ ...data, kind });
  };

  const submit = async (form) => {
    const ep = tab === "customers" ? "/crm/customers" : "/crm/suppliers";
    try {
      await api.post(ep, form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const submitReassign = async (cid, ownerId) => {
    try {
      await api.post(`/crm/customers/${cid}/reassign`, { owner_seller_id: ownerId || null });
      toast.success("Customer reassigned");
      setReassign(null);      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const list = tab === "customers"
    ? (tagFilter ? customers.filter((c) => c.tag === tagFilter) : customers)
    : suppliers;
  const Icon = tab === "customers" ? Users : Truck;

  const deleteContact = async (c, kind) => {
    if (!window.confirm(`Permanently delete ${c.name}? This cannot be undone.`)) return;
    const ep = kind === "customer" ? `/crm/customers/${c.id}` : `/crm/suppliers/${c.id}`;
    try {
      await api.delete(ep);
      toast.success(`${kind === "customer" ? "Customer" : "Supplier"} deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const segmentChips = useMemo(() => (
    ["VIP Collector", "Investor Buyer", "High Potential Client", "Casual Buyer"]
      .map((t) => ({ tag: t, count: segmentSummary[t] || 0 }))
  ), [segmentSummary]);

  return (
    <Layout
      title="CRM"
      subtitle="Relationships"
      action={
        canEdit && (
          <button
            data-testid="add-contact-btn"
            onClick={() => setOpen(true)}
            className="btn-gold px-5 py-3 inline-flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" />
            New {tab === "customers" ? "Customer" : "Supplier"}
          </button>
        )
      }
    >
      <Toaster theme="dark" position="top-right" />

      <div className="flex items-center gap-1 mb-8 border-b border-white/5">
        <TabBtn active={tab === "customers"} onClick={() => setTab("customers")} testid="tab-customers">
          <Users className="w-3.5 h-3.5 mr-2 inline" /> Customers
        </TabBtn>
        {user?.role !== "sales" && (
          <TabBtn active={tab === "suppliers"} onClick={() => setTab("suppliers")} testid="tab-suppliers">
            <Truck className="w-3.5 h-3.5 mr-2 inline" /> Suppliers
          </TabBtn>
        )}
      </div>

      {/* Segment Summary (customers tab only) */}
      {tab === "customers" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="segment-summary">
            {segmentChips.map(({ tag, count }) => {
              const meta = TAG_META[tag];
              const TagIcon = meta.icon;
              const active = tagFilter === tag;
              return (
                <button
                  key={tag}
                  data-testid={`segment-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setTagFilter(active ? "" : tag)}
                  className={`glass-card p-5 text-left transition-all ${active ? "ring-1 ring-[#D4AF37]" : "hover:border-white/15"}`}
                  style={active ? { borderColor: meta.color } : undefined}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div
                      className="w-9 h-9 flex items-center justify-center border"
                      style={{ borderColor: meta.color + "55", color: meta.color, backgroundColor: meta.bg }}
                    >
                      <TagIcon className="w-4 h-4" strokeWidth={1.25} />
                    </div>
                    <span className="mono text-2xl" style={{ color: meta.color }}>{count}</span>
                  </div>
                  <div className="text-xs uppercase tracking-[0.14em]" style={{ color: meta.color }}>{tag}</div>
                  <div className="text-[0.65rem] text-[#8A8A8A] mt-1">{meta.blurb}</div>
                </button>
              );
            })}
          </div>
          {tagFilter && (
            <div className="mb-6 flex items-center gap-3 text-xs">
              <span className="overline">Filtered by</span>
              <span
                className="px-3 py-1 border"
                style={{ borderColor: TAG_META[tagFilter].color + "55", color: TAG_META[tagFilter].color, backgroundColor: TAG_META[tagFilter].bg }}
              >
                {tagFilter}
              </span>
              <button
                onClick={() => setTagFilter("")}
                data-testid="clear-filter"
                className="text-[#8A8A8A] hover:text-[#EAEAEA] uppercase tracking-wider"
              >
                Clear
              </button>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#8A8A8A] glass-card">
            <Icon className="w-8 h-8 mx-auto mb-3 text-[#333]" strokeWidth={1} />
            No {tab}{tagFilter && tab === "customers" ? ` tagged as ${tagFilter}` : ""} yet.
          </div>
        )}
        {list.map((c) => {
          const meta = tab === "customers" ? TAG_META[c.tag || "New"] : null;
          const TagIcon = meta?.icon;
          const ownerName = c.owner_seller_id ? (sellerNameById[c.owner_seller_id] || "Unknown") : null;
          return (
          <div key={c.id} className="relative">
          <button
            onClick={() => openDetail(c, tab === "customers" ? "customer" : "supplier")}
            data-testid={`contact-${c.id}`}
            className="glass-card p-6 text-left transition-all hover:border-[#D4AF37]/30 w-full"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="min-w-0">
                <div className="serif text-xl truncate">{c.name}</div>
                {c.company && <div className="text-xs text-[#D4AF37] mt-1">{c.company}</div>}
              </div>
              <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] shrink-0">
                <Icon className="w-4 h-4" strokeWidth={1.25} />
              </div>
            </div>

            {/* Tag badge + stats for customers */}
            {tab === "customers" && meta && (
              <div className="mb-4 pb-4 border-b border-white/5">
                <div
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.62rem] uppercase tracking-[0.18em] border"
                  style={{ borderColor: meta.color + "55", color: meta.color, backgroundColor: meta.bg }}
                  data-testid={`tag-${c.id}`}
                >
                  <TagIcon className="w-3 h-3" strokeWidth={1.5} />
                  {c.tag}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-[0.6rem] text-[#666] uppercase tracking-wider">LTV</div>
                    <div className="mono text-[#EAEAEA] mt-0.5">{formatCurrency(c.total_spent)}</div>
                  </div>
                  <div>
                    <div className="text-[0.6rem] text-[#666] uppercase tracking-wider">Purchases</div>
                    <div className="mono text-[#EAEAEA] mt-0.5">{c.purchase_count}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Owner (admin only) */}
            {tab === "customers" && isAdmin && (
              <div className="mb-3 flex items-center gap-2 text-[0.7rem] text-[#8A8A8A]" data-testid={`owner-${c.id}`}>
                <UserCog className="w-3 h-3 text-[#D4AF37]" strokeWidth={1.25} />
                <span className="uppercase tracking-wider text-[0.6rem] text-[#666]">Owner</span>
                <span className={ownerName ? "text-[#D4AF37]" : "text-[#666] italic"}>
                  {ownerName || "Unassigned (admin pool)"}
                </span>
              </div>
            )}

            <div className="space-y-1.5 text-xs text-[#8A8A8A]">
              {c.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3" /> {c.email}</div>}
              {c.phone && <div className="flex items-center gap-2 mono"><Phone className="w-3 h-3" /> {c.phone}</div>}
            </div>
            {c.notes && (
              <div className="mt-4 pt-4 border-t border-white/5 text-xs text-[#666] italic line-clamp-2">"{c.notes}"</div>
            )}
          </button>
          {tab === "customers" && isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); setReassign(c); }}
              data-testid={`reassign-${c.id}`}
              className="absolute top-4 right-14 min-h-[44px] sm:min-h-0 px-2.5 py-1 text-[0.6rem] uppercase tracking-wider border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              title="Reassign owner"
            >
              Reassign
            </button>
          )}
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); deleteContact(c, tab === "customers" ? "customer" : "supplier"); }}
              data-testid={`delete-contact-${c.id}`}
              className="absolute top-4 right-4 min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 inline-flex items-center justify-center"
              title="Delete"
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.25} />
            </button>
          )}
          </div>
          );
        })}
      </div>

      {open && <ContactModal onClose={() => setOpen(false)} onSubmit={submit} kind={tab === "customers" ? "Customer" : "Supplier"} />}
      {detail && <DetailModal data={detail} onClose={() => setDetail(null)} />}
      {reassign && (
        <ReassignModal
          customer={reassign}
          salesUsers={salesUsers}
          onClose={() => setReassign(null)}
          onSubmit={submitReassign}
        />
      )}
    </Layout>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 text-xs uppercase tracking-[0.18em] relative ${active ? "text-[#D4AF37]" : "text-[#8A8A8A] hover:text-[#EAEAEA]"}`}
    >
      {children}
      {active && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#D4AF37]" />}
    </button>
  );
}

function ContactModal({ onClose, onSubmit, kind }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", address: "", notes: "" });
  const phoneRequired = kind === "Customer";
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-lg max-h-[92vh] overflow-y-auto fade-up" data-testid="contact-modal">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">New {kind}</div>
            <h3 className="serif text-2xl mt-1">Add Contact</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (phoneRequired && !form.phone.trim()) {
              toast.error("Mobile number is required for customers");
              return;
            }
            onSubmit(form);
          }}
          className="px-7 py-6 space-y-4"
        >
          {["name", "email", "phone", "company", "address"].map((k) => (
            <div key={k}>
              <div className="overline mb-2">
                {k}
                {(k === "name" || (k === "phone" && phoneRequired)) && (
                  <span className="text-[#D4AF37] ml-1">*</span>
                )}
                {k === "address" && <span className="text-[#666] ml-1 normal-case">(optional)</span>}
              </div>
              <input
                required={k === "name" || (k === "phone" && phoneRequired)}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="luxe-input w-full px-4 py-2.5 text-sm"
                data-testid={`contact-${k}`}
                placeholder={k === "phone" && phoneRequired ? "+62 ..." : ""}
              />
            </div>
          ))}
          <div>
            <div className="overline mb-2">Notes</div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm min-h-[80px]" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button type="submit" data-testid="submit-contact" className="btn-gold px-5 py-2.5">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetailModal({ data, onClose }) {
  const c = data.customer || data.supplier;
  const isCustomer = data.kind === "customer";
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto fade-up" data-testid="detail-modal">
        <div className="sticky top-0 bg-[#0F0F0F] border-b border-white/5 px-8 py-6 flex justify-between items-start">
          <div>
            <div className="overline">{isCustomer ? "Customer" : "Supplier"} · {isCustomer ? "Purchase History" : "Supply Record"}</div>
            <h2 className="serif text-3xl mt-1">{c.name}</h2>
            {isCustomer && data.tag && TAG_META[data.tag] && (
              <div className="mt-3">
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.62rem] uppercase tracking-[0.18em] border"
                  style={{
                    borderColor: TAG_META[data.tag].color + "55",
                    color: TAG_META[data.tag].color,
                    backgroundColor: TAG_META[data.tag].bg,
                  }}
                  data-testid="detail-tag"
                >
                  {data.tag}
                </span>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-[#8A8A8A]">
              {c.email && <span>{c.email}</span>}
              {c.phone && <span className="mono">{c.phone}</span>}
              {c.company && <span>{c.company}</span>}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <div className="px-8 py-6">
          <div className="grid grid-cols-3 gap-5 mb-8">
            <div className="border border-white/5 p-5">
              <div className="overline">{isCustomer ? "Total Spent" : "Total Cost"}</div>
              <div className="serif text-3xl mt-2 text-[#D4AF37]">{formatCurrency(isCustomer ? data.total_spent : data.total_cost)}</div>
            </div>
            <div className="border border-white/5 p-5">
              <div className="overline">{isCustomer ? "Purchases" : "Pieces Supplied"}</div>
              <div className="serif text-3xl mt-2">{data.count}</div>
            </div>
            {isCustomer && (
              <div className="border border-white/5 p-5">
                <div className="overline">Avg Ticket</div>
                <div className="serif text-3xl mt-2 text-[#7BA585]">{formatCurrency(data.avg_ticket || 0)}</div>
              </div>
            )}
          </div>

          <div className="overline mb-3">{isCustomer ? "Sales" : "Pieces"}</div>
          <div className="overflow-x-auto -mx-2 px-2">
          {isCustomer ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left overline text-[0.65rem] border-b border-white/5">
                  <th className="py-3 font-normal">Invoice</th>
                  <th className="py-3 font-normal">Date</th>
                  <th className="py-3 font-normal">Piece</th>
                  <th className="py-3 font-normal text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[#666]">No purchases yet.</td></tr>}
                {data.sales.map((s) => (
                  <tr key={s.id} className="border-b border-white/5">
                    <td className="py-3 mono text-xs text-[#D4AF37]">{s.invoice_number}</td>
                    <td className="py-3 mono text-xs text-[#8A8A8A]">{s.created_at?.slice(0, 10)}</td>
                    <td className="py-3">
                      {s.product_name}
                      <div className="text-[0.65rem] text-[#666] mono mt-0.5">
                        {formatQty(s.quantity, s.product_unit, s.product_custom_unit)}
                      </div>
                    </td>
                    <td className="py-3 mono text-right">{formatCurrency(s.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left overline text-[0.65rem] border-b border-white/5">
                  <th className="py-3 font-normal">SKU</th>
                  <th className="py-3 font-normal">Piece</th>
                  <th className="py-3 font-normal">Status</th>
                  <th className="py-3 font-normal text-right">Cost</th>
                  <th className="py-3 font-normal text-right w-12"></th>
                </tr>
              </thead>
              <tbody>
                {data.products.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#666]">No supplies recorded.</td></tr>}
                {data.products.map((p) => (
                  <tr key={p.id} className="border-b border-white/5">
                    <td className="py-3 mono text-xs text-[#D4AF37]">{p.sku}</td>
                    <td className="py-3">
                      {p.name}
                      <div className="text-[0.65rem] text-[#666] mono mt-0.5">
                        {formatQty(p.stock != null ? p.stock : p.quantity, p.stock_unit || p.unit, p.stock_custom_unit || p.custom_unit)}
                      </div>
                    </td>
                    <td className="py-3">
                      <span className={`status-badge status-${p.status?.toLowerCase()}`}>{p.status}</span>
                    </td>
                    <td className="py-3 mono text-right">{formatCurrency(p.cost_price)}</td>
                    <td className="py-3 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          onClose();
                          // Deep-link to Inventory and pop the edit modal for this product.
                          navigate(`/inventory?edit=${p.id}`);
                        }}
                        className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37]"
                        title="Edit this piece in Inventory"
                        data-testid={`supplier-piece-edit-${p.id}`}
                        aria-label="Edit piece"
                      >
                        <Edit className="w-3.5 h-3.5" strokeWidth={1.25} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>

          {/* Supplier-only: multi-currency purchase ledger */}
          {!isCustomer && (
            <PurchasesSection supplierId={c.id} />
          )}
        </div>
      </div>
    </div>
  );
}

const CURRENCIES = ["USD", "THB", "INR", "IDR"];

function fmtNative(amount, currency) {
  // Simple currency formatter — IDR uses dot grouping, others use comma.
  if (currency === "IDR") return `Rp ${Number(amount).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;
  const sym = { USD: "$", THB: "฿", INR: "₹" }[currency] || "";
  return `${sym}${Number(amount).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

function PurchasesSection({ supplierId }) {
  const [data, setData] = useState({ purchases: [], payments: [], total_idr: 0, paid_idr: 0, balance_idr: 0, count: 0 });
  const [openPurchase, setOpenPurchase] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);  // {purchase obj} when editing
  const [openPayment, setOpenPayment] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const res = await api.get(`/crm/suppliers/${supplierId}/purchases`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load purchases");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [supplierId]);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete purchase: ${p.item}?`)) return;
    try {
      await api.delete(`/crm/purchases/${p.id}`);
      toast.success("Purchase removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const onDeletePayment = async (pay) => {
    if (!window.confirm(`Reverse payment ${fmtNative(pay.amount_native, pay.currency)} (${formatCurrency(pay.amount_idr)})?`)) return;
    try {
      await api.delete(`/crm/suppliers/${supplierId}/payments/${pay.id}`);
      toast.success("Payment reversed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const fullyPaid = data.balance_idr <= 0;

  const onDownloadPdf = async () => {
    try {
      const blob = await api.get(`/crm/suppliers/${supplierId}/statement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(blob.data);
      window.open(url, "_blank", "noopener");
      // Defer revoke so the new tab has time to load it
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate statement");
    }
  };

  const onWhatsAppShare = async () => {
    try {
      const res = await api.post(`/crm/suppliers/${supplierId}/statement/share`);
      const wa = res.data.whatsapp_url;
      if (wa) {
        window.open(wa, "_blank", "noopener");
        toast.success("WhatsApp opened — message pre-filled");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate share link");
    }
  };

  // Pretty-print native-currency totals: only show currencies that actually
  // have any volume (purchased OR paid) so the strip stays compact.
  const native = data.native_breakdown || { purchases: {}, paid: {} };
  const nativeCcys = Array.from(
    new Set([...Object.keys(native.purchases || {}), ...Object.keys(native.paid || {})])
  ).filter((c) => (native.purchases?.[c] || 0) > 0 || (native.paid?.[c] || 0) > 0);

  return (
    <div className="mt-10">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="overline">Purchase Ledger ({data.count})</div>
          <div className="text-[0.65rem] mono text-[#8A8A8A] mt-0.5">
            Running account · supplier-level balance
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onDownloadPdf}
            data-testid="supplier-statement-pdf-btn"
            className="min-h-[44px] px-4 py-2 text-xs inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
            title="Download statement of account"
          >
            <FileText className="w-3.5 h-3.5" /> Statement PDF
          </button>
          <button
            onClick={onWhatsAppShare}
            data-testid="supplier-whatsapp-btn"
            className="min-h-[44px] px-4 py-2 text-xs inline-flex items-center gap-2 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10"
            title="Send statement to supplier via WhatsApp"
          >
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </button>
          <button
            onClick={() => setOpenPayment(true)}
            disabled={fullyPaid}
            data-testid="add-supplier-payment-btn"
            className="btn-outline-gold min-h-[44px] px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title={fullyPaid ? "All purchases settled" : "Record a payment against this supplier"}
          >
            <Plus className="w-3.5 h-3.5" /> Record Payment
          </button>
          <button
            onClick={() => setOpenPurchase(true)}
            data-testid="add-purchase-btn"
            className="btn-gold min-h-[44px] px-4 py-2 text-xs inline-flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> Add Purchase
          </button>
        </div>
      </div>

      {/* Running-account tiles */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="border border-white/5 bg-[#0F0F0F] p-4" data-testid="purchases-total-tile">
          <div className="overline text-[0.55rem]">Total Purchases</div>
          <div className="serif text-xl mt-1.5 text-[#EAEAEA]">{formatCurrency(data.total_idr)}</div>
        </div>
        <div className="border border-[#7BA585]/30 bg-[#7BA585]/[0.05] p-4" data-testid="purchases-paid-tile">
          <div className="overline text-[0.55rem] text-[#7BA585]">Paid</div>
          <div className="serif text-xl mt-1.5 text-[#7BA585]">{formatCurrency(data.paid_idr)}</div>
        </div>
        <div className={`border p-4 ${fullyPaid ? "border-[#7BA585]/30 bg-[#7BA585]/[0.05]" : "border-[#D4AF37]/30 bg-[#D4AF37]/[0.05]"}`} data-testid="purchases-balance-tile">
          <div className={`overline text-[0.55rem] ${fullyPaid ? "text-[#7BA585]" : "text-[#D4AF37]"}`}>Balance Due</div>
          <div className={`serif text-xl mt-1.5 ${fullyPaid ? "text-[#7BA585]" : "text-[#D4AF37]"}`}>{formatCurrency(data.balance_idr)}</div>
        </div>
      </div>

      {/* Native-currency strip — purely informational. Only renders when there
          is at least one foreign-currency transaction so it doesn't clutter
          plain IDR suppliers. */}
      {nativeCcys.length > 0 && (
        <div className="border border-white/5 bg-[#0A0A0A] p-3 mb-5" data-testid="purchases-native-strip">
          <div className="overline text-[0.55rem] mb-2">Native Currency Totals</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {nativeCcys.map((ccy) => (
              <div key={ccy} className="flex items-baseline justify-between gap-2 mono text-xs" data-testid={`native-strip-${ccy}`}>
                <span className="text-[#8A8A8A]">{ccy}</span>
                <span className="text-right">
                  <span className="text-[#EAEAEA]">{fmtNative(native.purchases?.[ccy] || 0, ccy)}</span>
                  {(native.paid?.[ccy] || 0) > 0 && (
                    <span className="text-[#7BA585]"> · paid {fmtNative(native.paid[ccy], ccy)}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="overflow-x-auto -mx-2 px-2">
        <table className="w-full text-sm min-w-[640px]" data-testid="purchases-table">
          <thead>
            <tr className="text-left overline text-[0.65rem] border-b border-white/5">
              <th className="py-3 font-normal">Date</th>
              <th className="py-3 font-normal">Item</th>
              <th className="py-3 font-normal text-right">Qty</th>
              <th className="py-3 font-normal text-right">Unit Price</th>
              <th className="py-3 font-normal text-right">Native Total</th>
              <th className="py-3 font-normal text-right">IDR Equivalent</th>
              {isAdmin && <th className="py-3" />}
            </tr>
          </thead>
          <tbody>
            {data.purchases.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="py-6 text-center text-[#666]">No purchases recorded yet.</td></tr>
            )}
            {data.purchases.map((p) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="py-3 mono text-xs text-[#8A8A8A]">{(p.date || "").slice(0, 10)}</td>
                <td className="py-3">
                  {p.item}
                  {p.notes && <div className="text-[0.65rem] text-[#666] mt-0.5 italic">{p.notes}</div>}
                </td>
                <td className="py-3 mono text-right">{formatQty(p.quantity, p.unit, p.custom_unit)}</td>
                <td className="py-3 mono text-right">{fmtNative(p.unit_price, p.currency)}</td>
                <td className="py-3 mono text-right">
                  {fmtNative(p.native_total, p.currency)}
                  <div className="text-[0.6rem] text-[#666] mt-0.5">@ {p.fx_rate_to_idr.toLocaleString("id-ID")}</div>
                </td>
                <td className="py-3 mono text-right text-[#D4AF37]">{formatCurrency(p.idr_total)}</td>
                {isAdmin && (
                  <td className="py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setEditingPurchase(p)}
                      data-testid={`edit-purchase-${p.id}`}
                      className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-[#D4AF37]"
                      aria-label="Edit purchase"
                      title="Edit this purchase row"
                    >
                      <Edit className="w-3.5 h-3.5" strokeWidth={1.25} />
                    </button>
                    <button
                      onClick={() => onDelete(p)}
                      data-testid={`delete-purchase-${p.id}`}
                      className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400"
                      aria-label="Delete purchase"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Supplier-level Payment History */}
      <div className="mt-7">
        <div className="overline mb-3">Payment History ({data.payments?.length || 0})</div>
        {(!data.payments || data.payments.length === 0) ? (
          <div className="text-sm text-[#666] italic">No payments yet — entire balance on credit.</div>
        ) : (
          <div className="space-y-2" data-testid="supplier-payments-list">
            {data.payments.map((p) => (
              <div key={p.id} className="border border-white/5 p-3 flex justify-between items-start gap-3" data-testid={`supplier-payment-row-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="serif text-lg">{fmtNative(p.amount_native, p.currency)}</span>
                    {p.currency !== "IDR" && (
                      <span className="mono text-[0.65rem] text-[#8A8A8A]">≈ {formatCurrency(p.amount_idr)}</span>
                    )}
                    <span className="text-[0.6rem] uppercase tracking-wider px-2 py-0.5 border border-[#D4AF37]/30 text-[#D4AF37]">{p.type}</span>
                    <span className="text-[0.6rem] uppercase tracking-wider text-[#8A8A8A]">· {p.method}</span>
                  </div>
                  <div className="text-[0.65rem] mono text-[#8A8A8A] mt-1">
                    {(p.date || "").slice(0, 10)}{p.recorded_by ? ` · by ${p.recorded_by}` : ""}
                    {p.currency !== "IDR" && ` · @ FX ${Number(p.fx_rate_to_idr || 0).toLocaleString("id-ID")}`}
                  </div>
                  {p.note && <div className="text-[0.7rem] text-[#8A8A8A] mt-1 italic">{p.note}</div>}
                </div>
                {isAdmin && (
                  <button
                    onClick={() => onDeletePayment(p)}
                    data-testid={`delete-supplier-payment-${p.id}`}
                    className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 shrink-0"
                    aria-label="Reverse payment"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {openPurchase && (
        <PurchaseModal
          supplierId={supplierId}
          onClose={() => setOpenPurchase(false)}
          onSaved={() => { setOpenPurchase(false); load(); }}
        />
      )}
      {editingPurchase && (
        <PurchaseModal
          supplierId={supplierId}
          purchase={editingPurchase}
          onClose={() => setEditingPurchase(null)}
          onSaved={() => { setEditingPurchase(null); load(); }}
        />
      )}
      {openPayment && (
        <SupplierPaymentModal
          supplierId={supplierId}
          balanceDue={data.balance_idr}
          onClose={() => setOpenPayment(false)}
          onSaved={() => { setOpenPayment(false); load(); }}
        />
      )}
    </div>
  );
}

const DEFAULT_RATES = {
  USD: 16000,
  THB: 456,
  INR: 190,
  IDR: 1,
};

function PurchaseModal({ supplierId, purchase = null, onClose, onSaved }) {
  // When `purchase` is provided we're editing an existing ledger row → PUT.
  // Sync-to-inventory checkbox is hidden in edit mode because the inventory
  // sync only runs on creation; editing a purchase doesn't retroactively
  // create or mutate the synced product.
  const isEdit = Boolean(purchase?.id);
  const [form, setForm] = useState({
    date: (purchase?.date || new Date().toISOString().slice(0, 10)).slice(0, 10),
    item: purchase?.item || "",
    quantity: purchase?.quantity ?? 1,
    unit: purchase?.unit || "pcs",
    custom_unit: purchase?.custom_unit || "",
    currency: purchase?.currency || "IDR",
    unit_price: purchase?.unit_price ?? 0,
    fx_rate_to_idr: purchase?.fx_rate_to_idr ?? 1,
    payment_method: purchase?.payment_method || "Wire",
    notes: purchase?.notes || "",
    sync_to_inventory: true,
  });
  const [submitting, setSubmitting] = useState(false);
  // When editing, operator can opt in to cascade the new cost to the linked
  // SKU (back-linked via source_purchase_id). Default OFF — editing a purchase
  // alone never mutates inventory.
  const [alsoUpdateSku, setAlsoUpdateSku] = useState(false);

  // Auto-suggest FX when currency changes (user can still override).
  const setCurrency = (cur) => {
    setForm((f) => ({ ...f, currency: cur, fx_rate_to_idr: DEFAULT_RATES[cur] ?? 1 }));
  };

  const native = (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_price) || 0);
  const idr = native * (parseFloat(form.fx_rate_to_idr) || 0);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: parseFloat(form.quantity),
        unit_price: parseFloat(form.unit_price),
        fx_rate_to_idr: parseFloat(form.fx_rate_to_idr) || 1,
      };
      if (isEdit) {
        const { data } = await api.put(`/crm/purchases/${purchase.id}`, {
          ...payload,
          also_update_linked_sku: alsoUpdateSku,
        });
        const synced = data?.synced_sku_updated;
        if (synced) {
          toast.success(`Purchase updated · ${synced.sku} cost now ${formatCurrency(synced.cost_price)}/piece`);
        } else if (alsoUpdateSku) {
          toast.success(`Purchase updated · (no linked SKU found to update)`);
        } else {
          toast.success(`Purchase updated · ${fmtNative(native, form.currency)} → ${formatCurrency(idr)}`);
        }
      } else {
        const { data } = await api.post(`/crm/suppliers/${supplierId}/purchases`, payload);
        const synced = data?.synced_product;
        if (synced) {
          toast.success(
            `Purchase added · ${synced.sku} synced to inventory (${synced.stock} ${form.unit === "pcs" ? "pcs" : form.unit} · cost ${formatCurrency(synced.cost_price)}/piece)`,
          );
        } else {
          toast.success(`Purchase added · ${fmtNative(native, form.currency)} → ${formatCurrency(idr)}`);
        }
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-2xl max-h-[92vh] overflow-y-auto fade-up">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">{isEdit ? "Edit Purchase" : "New Purchase"}</div>
            <h3 className="serif text-2xl mt-1">{isEdit ? "Update Supplier Cost" : "Record Supplier Cost"}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="purchase-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="overline block mb-2">Date</label>
              <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="purchase-date" />
            </div>
            <div className="sm:col-span-2">
              <label className="overline block mb-2">Item Description</label>
              <input required value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="e.g. Rough Sapphire 5ct" className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="purchase-item" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="overline block mb-2">Quantity</label>
              <input type="number" step="0.01" min="0.01" required value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="purchase-qty" />
            </div>
            <div>
              <label className="overline block mb-2">Unit</label>
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="purchase-unit">
                {["ct", "g", "kg", "pcs", "custom"].map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Currency</label>
              <select value={form.currency} onChange={(e) => setCurrency(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="purchase-currency">
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Unit Price ({form.currency})</label>
              <input type="number" step="0.01" min="0" required value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm mono" data-testid="purchase-unit-price" />
            </div>
          </div>
          {form.unit === "custom" && (
            <div>
              <label className="overline block mb-2">Custom Unit Label</label>
              <input value={form.custom_unit} onChange={(e) => setForm({ ...form, custom_unit: e.target.value })} placeholder="e.g. tola" className="luxe-input w-full px-4 py-2.5 text-sm" />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-2">
                FX Rate <span className="text-[#666] normal-case tracking-normal">(1 {form.currency} = ? IDR)</span>
              </label>
              <input
                type="number" step="0.0001" min="0.0001" required
                value={form.fx_rate_to_idr}
                onChange={(e) => setForm({ ...form, fx_rate_to_idr: e.target.value })}
                disabled={form.currency === "IDR"}
                className="luxe-input w-full px-4 py-2.5 text-sm mono disabled:opacity-50"
                data-testid="purchase-fx-rate"
              />
              {form.currency !== "IDR" && (
                <div className="text-[0.6rem] text-[#666] mt-1">Default {DEFAULT_RATES[form.currency]} · adjust to your invoice rate</div>
              )}
            </div>
            <div>
              <label className="overline block mb-2">Payment</label>
              <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm">
                {["Cash", "Wire", "Card", "Crypto"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-2">Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" />
          </div>

          {/* Auto-sync to inventory toggle (only on create — sync runs once at creation) */}
          {!isEdit && (
            <label
              className="flex items-start gap-3 px-4 py-3 border border-[#D4AF37]/20 bg-[#D4AF37]/[0.03] cursor-pointer hover:border-[#D4AF37]/40 transition-colors"
              data-testid="purchase-sync-toggle-label"
            >
              <input
                type="checkbox"
                checked={form.sync_to_inventory}
                onChange={(e) => setForm({ ...form, sync_to_inventory: e.target.checked })}
                className="accent-[#D4AF37] mt-0.5 shrink-0"
                data-testid="purchase-sync-toggle"
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-[#EAEAEA]">Also add to inventory</div>
                <div className="text-[0.65rem] text-[#8A8A8A] mt-0.5 leading-relaxed">
                  {form.sync_to_inventory ? (
                    <>Creates a new SKU in Inventory with <span className="mono text-[#D4AF37]">stock = {form.unit === "pcs" || form.unit === "custom" ? `${form.quantity} ${form.unit}` : `1 pcs (${form.quantity} ${form.unit} per piece)`}</span> and supplier auto-linked. Refine photos &amp; selling price later.</>
                  ) : (
                    "Pure ledger entry — no inventory impact. Use for tools, supplies, services."
                  )}
                </div>
              </div>
            </label>
          )}

          {/* Cascade-to-SKU toggle (only on edit) */}
          {isEdit && (
            <label
              className="flex items-start gap-3 px-4 py-3 border border-[#C99356]/30 bg-[#C99356]/[0.04] cursor-pointer hover:border-[#C99356]/50 transition-colors"
              data-testid="purchase-cascade-sku-label"
            >
              <input
                type="checkbox"
                checked={alsoUpdateSku}
                onChange={(e) => setAlsoUpdateSku(e.target.checked)}
                className="accent-[#C99356] mt-0.5 shrink-0"
                data-testid="purchase-cascade-sku-toggle"
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs text-[#EAEAEA]">Also update linked SKU&apos;s cost</div>
                <div className="text-[0.65rem] text-[#8A8A8A] mt-0.5 leading-relaxed">
                  {alsoUpdateSku ? (
                    <>Pushes the new per-piece cost (<span className="mono text-[#C99356]">{formatCurrency(idr)} ÷ stock</span>) into Inventory for the auto-synced product back-linked to this purchase. Stock count is <strong>not</strong> changed. No-op if no linked SKU exists.</>
                  ) : (
                    "Off — Inventory cost stays as-is. Only the ledger row is edited."
                  )}
                </div>
              </div>
            </label>
          )}

          {/* Totals preview */}
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 p-5 grid grid-cols-2 gap-3" data-testid="purchase-totals">
            <div>
              <div className="overline text-[0.6rem]">Native Total</div>
              <div className="serif text-xl mt-1">{fmtNative(native, form.currency)}</div>
            </div>
            <div className="text-right">
              <div className="overline text-[0.6rem]">IDR Equivalent</div>
              <div className="serif text-xl mt-1 text-[#D4AF37]">{formatCurrency(idr)}</div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold min-h-[44px] px-5 py-2.5">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-gold min-h-[44px] px-5 py-2.5" data-testid="submit-purchase">
              {submitting ? "Saving…" : (isEdit ? "Save Changes" : "Add Purchase")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ReassignModal({ customer, salesUsers, onClose, onSubmit }) {
  const [ownerId, setOwnerId] = useState(customer.owner_seller_id || "");
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-md fade-up" data-testid="reassign-modal">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Reassign Customer</div>
            <h3 className="serif text-2xl mt-1">{customer.name}</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(customer.id, ownerId); }}
          className="px-7 py-6 space-y-4"
        >
          <div>
            <div className="overline mb-2">New Owner</div>
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="luxe-input w-full px-4 py-2.5 text-sm"
              data-testid="reassign-owner"
            >
              <option value="">— Unassigned (admin pool) —</option>
              {salesUsers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <div className="text-[0.65rem] text-[#666] mt-2">
              Reassignment is logged in the audit trail. The new owner will see this customer in their CRM; the previous owner loses access.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button type="submit" data-testid="submit-reassign" className="btn-gold px-5 py-2.5">Reassign</button>
          </div>
        </form>
      </div>
    </div>
  );
}



function SupplierPaymentModal({ supplierId, balanceDue, onClose, onSaved }) {
  // One payment, applied at the supplier level. Auto-reduces the running balance.
  // User may pay in any currency (USD/THB/INR/IDR) with FX captured at payment time.
  const [form, setForm] = useState({
    amount: "",
    currency: "IDR",
    fx_rate_to_idr: 1,
    type: "Installment",
    method: "Wire",
    note: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Sensible FX defaults whenever the user flips currency.
  const FX_DEFAULTS = { USD: 16000, THB: 456, INR: 190, IDR: 1 };
  useEffect(() => {
    setForm((f) => ({
      ...f,
      fx_rate_to_idr: f.currency === "IDR" ? 1 : (FX_DEFAULTS[f.currency] || 1),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.currency]);

  const fx = Number(form.fx_rate_to_idr) || 0;
  const amt = Number(form.amount) || 0;
  const idrEquivalent = form.currency === "IDR" ? amt : Math.round(amt * fx);
  const remainingAfter = Math.max(0, balanceDue - idrEquivalent);
  const overpay = idrEquivalent > balanceDue + 0.5;

  const submit = async (e) => {
    e.preventDefault();
    if (amt <= 0) { toast.error("Enter an amount > 0"); return; }
    setSubmitting(true);
    try {
      await api.post(`/crm/suppliers/${supplierId}/payments`, {
        amount: amt,
        currency: form.currency,
        fx_rate_to_idr: form.currency === "IDR" ? 1 : fx,
        type: form.type,
        method: form.method,
        note: form.note,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="supplier-payment-modal">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-xl max-h-[92vh] overflow-y-auto fade-up">
        <div className="sticky top-0 z-10 bg-[#0F0F0F] border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline text-[#D4AF37]">Supplier Payment</div>
            <h3 className="serif text-2xl mt-1">Record Payment</h3>
            <div className="text-[0.65rem] mono text-[#8A8A8A] mt-1">
              Outstanding balance: <span className="text-[#D4AF37]">{formatCurrency(balanceDue)}</span>
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-[#8A8A8A]" /></button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="supplier-payment-form">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className="overline block mb-2">Amount *</label>
              <input
                type="number" step="0.01" min="0.01" required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="luxe-input w-full px-4 py-2.5 text-sm mono"
                data-testid="supplier-payment-amount"
              />
            </div>
            <div>
              <label className="overline block mb-2">Currency</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="luxe-input w-full px-4 py-2.5 text-sm"
                data-testid="supplier-payment-currency"
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">FX → IDR</label>
              <input
                type="number" step="0.01" min="0.01"
                value={form.fx_rate_to_idr}
                disabled={form.currency === "IDR"}
                onChange={(e) => setForm({ ...form, fx_rate_to_idr: e.target.value })}
                className="luxe-input w-full px-4 py-2.5 text-sm mono disabled:opacity-50"
                data-testid="supplier-payment-fx"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <label className="overline block mb-2">Type</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="supplier-payment-type">
                <option value="Deposit">Deposit</option>
                <option value="Installment">Installment</option>
                <option value="Balance">Balance</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Method</label>
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="supplier-payment-method">
                <option value="Wire">Wire</option>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Crypto">Crypto</option>
              </select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="overline block mb-2">Note</label>
              <input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="optional"
                className="luxe-input w-full px-4 py-2.5 text-sm"
                data-testid="supplier-payment-note"
              />
            </div>
          </div>
          <div className="text-xs text-[#8A8A8A] mono flex flex-wrap gap-x-4 gap-y-1 border border-white/5 p-3">
            <span>IDR equivalent: <span className="text-[#D4AF37]">{formatCurrency(idrEquivalent)}</span></span>
            <span>Balance after: <span className={remainingAfter > 0 ? "text-[#C99356]" : "text-[#7BA585]"}>{formatCurrency(remainingAfter)}</span></span>
            {overpay && <span className="text-[#C99356]">(Will be capped at balance due)</span>}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button
              type="submit"
              disabled={submitting || amt <= 0}
              className="btn-gold px-5 py-2.5 disabled:opacity-50"
              data-testid="submit-supplier-payment"
            >
              {submitting ? "Recording…" : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

